import { USE_MOCKS } from '@/config';
import { ApiError, delay, request } from './client';

/**
 * Signs in against the external auth service, brokered by our backend.
 *
 * The provider authenticates by username, not email — so this is a login ID,
 * and it is the same ID the purchase is recorded against in playTUB. Credentials
 * go straight through to /api/auth/login and are never stored client-side; only
 * the session token that comes back is kept.
 */
export async function login({ username, password }, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(300);
    // No auth service to talk to in mock mode, so the check is only that the
    // form was filled in — enough to exercise the real sign-in flow offline.
    if (!username?.trim() || !password) {
      throw new ApiError("That username or password isn't right.", 401, null);
    }
    return {
      access_token: `mock-token-for-${username.trim()}`,
      token_type: 'bearer',
      user: { id: username.trim(), username: username.trim(), display_name: username.trim() },
    };
  }

  return request('/api/auth/login', {
    method: 'POST',
    body: { username: username.trim(), password },
    signal,
    anonymous: true,
  });
}

/**
 * Opens a session from a playTUB handoff ticket, no credentials involved.
 *
 * A learner who is already signed in to playTUB gets here from its profile menu,
 * carrying a one-time ticket. Our backend is what redeems it against the auth
 * service; the ticket only passes through here. It is spent by that exchange and
 * expires within about a minute, so a stale link is a rejection, not a way in.
 */
export async function exchangeSsoTicket(ticket, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(200);
    // Nothing to redeem a ticket against offline. Fail the way a real expired
    // ticket does, so the landing page's error path is what gets exercised.
    throw new ApiError('That sign-in link has expired. Please sign in.', 401, null);
  }

  return request('/api/auth/sso', {
    method: 'POST',
    body: { ticket },
    signal,
    anonymous: true,
  });
}

/** The signed-in user behind the current token; 401s when it has expired. */
export async function me({ signal } = {}) {
  if (USE_MOCKS) {
    await delay(80);
    throw new ApiError('No session.', 401, null);
  }
  return request('/api/auth/me', { signal });
}

/**
 * Creates a playTUB account from the storefront.
 *
 * Not a marketplace-only login: our backend creates it on the same external auth
 * service /login authenticates against, with the same "global user, no
 * organisation" shape playTUB's own sign-up uses. So a reader who registers here
 * can sign in to playTUB with these credentials, and what they buy unlocks on
 * that one account.
 *
 * `dob` is required (ISO YYYY-MM-DD): the storefront sells to adults, so an
 * account cannot be created without the buyer's age on it.
 *
 * No session comes back — registering and signing in stay separate calls, and
 * AuthContext.signUp is what puts them together.
 */
export async function register(details, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(300);
    if (!details.username?.trim() || !details.password) {
      throw new ApiError('Please choose a username and a password.', 400, null);
    }
    // The real endpoint requires a date of birth — the storefront sells to
    // adults — so mock mode refuses a signup without one too, rather than
    // letting an offline run pass what a live one would reject.
    if (!details.dob) {
      throw new ApiError('Please enter your date of birth.', 422, null);
    }
    return { message: 'Account created.', username: details.username.trim().toLowerCase() };
  }

  return request('/api/auth/register', {
    method: 'POST',
    body: {
      ...details,
      username: details.username.trim(),
    },
    signal,
    anonymous: true,
  });
}

/**
 * Whether a username is already taken.
 *
 * A courtesy for the sign-up form — being told while typing beats being told
 * after filling the rest in. Never a gate: the answer is only advisory (the
 * service that holds it may be down or throttling, in which case the backend
 * reports the name as free), and `register` is what actually decides.
 */
export async function checkUsername(username, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(150);
    return { exists: false };
  }

  return request(`/api/auth/check-username?username=${encodeURIComponent(username.trim())}`, {
    signal,
    anonymous: true,
  });
}
